/**
 * A contract's OPEN and CLOSE in History Lens - Archive Search, Contract Timeline and Multi-car (owner, 6 Oct 2026:
 * "اى عقد يعتمد فى فتح العقد فى اى مكان على ايجار لانه اللى بيتفتح الاول وفى الاغلاق يعتمد على ال اي شيك لانه غالبا
 * بيكون الاول ولو مفيش فى الاغلاق اي شيك يعتمد على صفحة ايجار يعنى فى كلا الاحوال ايهما الاول يعتمد عليه"):
 *   open  = the earlier of Ejar's pick-up and the contract's first E-Check "Create Contract"; no check at all (staff
 *           skipped it - CC 32549, I2610000D02497877 opened on Ejar 05/10 12:07 with no E-Check): Ejar's alone
 *   close = the earlier of the last E-Check "Close Contract" and Ejar's drop-off once Ejar has it closed; no close check:
 *           Ejar's alone
 * The E-Check rows keep their own times as 'E-Check Time' when Ejar's is earlier ('Source': 'Ejar'); a row made from
 * Ejar alone has no check behind it (`_ejarOnly`: no damage audit, no E-Check number). Car swaps in between keep the
 * E-Check times - only the contract's first open and last close are Ejar's.
 */
import { parseSheetDate } from '../pages/MultiCar/utils/dates';

const CLOSED_STATUS = /closed|delivered|returned|completed|cancel/i;
const pad = (n) => String(n).padStart(2, '0');

/** An instant (ms) as the archive's "dd/mm/yyyy HH:MM" Dubai text. */
export const archiveText = (ms) => {
    const d = new Date(ms + 4 * 60 * 60 * 1000);
    return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

const isoMs = (iso) => {
    const t = Date.parse(iso || '');
    return Number.isFinite(t) ? t : null;
};

/** A row's instant (ms): the store's own `_at`, else its "Transaction Date" text; null when unreadable. */
export const rowMs = (r) => {
    if (Number.isFinite(r?._at)) return r._at;
    const d = parseSheetDate(r?.['Transaction Date']);
    return d ? d.getTime() : null;
};

/** Ejar's E-Check number (the store's check id when there is none): the order of two checks in the same minute. */
const seqOf = (r) => {
    const n = Number(String(r?.['E-Check Number'] ?? '').trim());
    if (Number.isFinite(n) && n > 0) return n;
    const id = Number(r?.check_id);
    return Number.isFinite(id) ? id : 0;
};

/** 'open' | 'close' | '' - the archive's check types ("Create Contract", "Close Contract", "Cancel ..."). */
export const checkKindOf = (r) => {
    const t = String(r?.['Check Type'] || '').toLowerCase();
    if (t.includes('create') || t.includes('open')) return 'open';
    if (t.includes('close') || t.includes('cancel')) return 'close';
    return '';
};

/** Ejar says the contract is over (Delivered - Paid / Indebt, Closed, Cancelled; the engine's E-Check close too). */
export const isEjarClosed = (record) => CLOSED_STATUS.test(String(record?.status || ''));

const keyOf = (v) => String(v || '').trim().toUpperCase();

// earlier by a whole minute at least: the archive shows minutes, and Ejar's drop-off is often the close check's own
// moment a second before it (10:51:08 / 10:51:09) - no "Ejar" mark for the same minute
const minuteBefore = (ms, than) => than === null || Math.floor(ms / 60000) < Math.floor(than / 60000);

// up to an hour between Ejar and the E-Check is the usual time a check takes (owner, 6 Oct 2026: "خلى الفرق لو ساعه
// يبقى عادى لان غالبا ال Echeck بيحصل فى المدة دى") - Ejar's time is still the one used, only a longer gap is pointed out
export const NORMAL_GAP_MS = 60 * 60 * 1000;

/** The E-Check time is worth showing beside Ejar's: no check at all, or more than an hour between them. */
export const echeckGapShown = (row) => row?.Source === 'Ejar' && (row._ejarOnly || (row._gapMs || 0) > NORMAL_GAP_MS);

const takeEjarTime = (row, ms) => {
    row._gapMs = Math.abs((rowMs(row) ?? ms) - ms);
    row['E-Check Time'] = row['Transaction Date'];
    row['Transaction Date'] = archiveText(ms);
    row._at = ms;
    row.Source = 'Ejar';
};

/**
 * E-Check rows + the store's contract records (v2: contract_no, pickup_date / dropoff_date ISO, status, car,
 * first_car, branch, return_branch, customer) -> the rows with each contract's open and close by the owner's rule.
 * Pure: the rows given are copied, never changed. A record with no rows gives its contract's Ejar open (and close).
 */
export function ejarOpenClose(rows, records, now = Date.now()) {
    const byNo = new Map();
    (records || []).forEach((v) => { if (v?.contract_no) byNo.set(keyOf(v.contract_no), v); });
    const out = (rows || []).map((r) => ({ ...r }));
    const groups = new Map();
    out.forEach((r) => {
        const k = keyOf(r['Contract No.']);
        if (!k) return;
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push(r);
    });
    byNo.forEach((_v, k) => { if (!groups.has(k)) groups.set(k, []); });
    const added = [];
    groups.forEach((list, k) => {
        const v = byNo.get(k);
        if (!v) return;
        list.sort((a, b) => ((rowMs(a) ?? 0) - (rowMs(b) ?? 0)) || (seqOf(a) - seqOf(b)));
        const base = { 'Contract No.': v.contract_no, 'Customer Name': v.customer?.name || '', 'Has Damages': '',
            'E-Check Number': '', Source: 'Ejar', _ejarOnly: true };
        const pick = isoMs(v.pickup_date);
        if (pick !== null) {
            const first = list[0];
            const firstAt = first ? rowMs(first) : null;
            if (first && checkKindOf(first) === 'open') {
                if (minuteBefore(pick, firstAt)) takeEjarTime(first, pick);
            } else if (firstAt === null || pick <= firstAt) {
                const car = v.first_car || v.car || {};
                // the next check's plate as it is written there (the pages pair rows by the exact plate text - review)
                added.push({ ...base, 'Check Type': 'Create Contract', 'Plate Number': first?.['Plate Number'] || car.plate || '',
                    Model: car.model || '', Branch: v.branch || '', 'Transaction Date': archiveText(pick), _at: pick });
            }
        }
        // a closed contract's drop-off - never one still to come (a cancelled contract keeps its planned date - review)
        const dropAt = isEjarClosed(v) ? isoMs(v.dropoff_date) : null;
        const drop = dropAt !== null && dropAt <= now ? dropAt : null;
        if (drop !== null) {
            const last = list[list.length - 1];
            const lastAt = last ? rowMs(last) : null;
            if (last && checkKindOf(last) === 'close') {
                // never before the check ahead of it (a car's create): only the close's own time can move earlier
                const before = list.length > 1 ? rowMs(list[list.length - 2]) : pick;
                if (minuteBefore(drop, lastAt) && (before === null || drop >= before)) takeEjarTime(last, drop);
            } else if ((lastAt === null || drop >= lastAt) && (pick === null || drop >= pick)) {
                added.push({ ...base, 'Check Type': 'Close Contract', 'Plate Number': last?.['Plate Number'] || v.car?.plate || '',
                    Model: last?.Model || v.car?.model || '', Branch: v.return_branch || v.branch || '',
                    'Transaction Date': archiveText(drop), _at: drop });
            }
        }
    });
    return [...out, ...added];
}

/** Newest first (the archive's pages - owner, 6 Oct 2026: "الارشيف المفروض يظهر الحديث فى الاول"). */
export const newestFirst = (rows) => [...(rows || [])].sort((a, b) => ((rowMs(b) ?? 0) - (rowMs(a) ?? 0)) || (seqOf(b) - seqOf(a)));
